import { useQueryClient } from "@tanstack/react-query";
import { analyticsAccessKey } from "../lib/analytics/access-key.js";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { JSX, ReactNode } from "react";

import { authApi } from "../lib/api/auth.js";
import { setCsrfToken } from "../lib/api/_client.js";
import {
  useLoginMutation,
  useLogoutMutation,
  useMeQuery,
  type PublicUser,
} from "../lib/api/auth.js";

// Access JWT lives ~15 min; refresh a bit ahead so an active tab never expires.
const PROACTIVE_REFRESH_MS = 10 * 60 * 1000;

interface AuthState {
  user: PublicUser | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const meQuery = useMeQuery();
  const queryClient = useQueryClient();
  const scopeKey = analyticsAccessKey(meQuery.data ?? null);
  const previousScope = useRef(scopeKey);
  useEffect(() => {
    if (previousScope.current !== scopeKey) {
      previousScope.current = scopeKey;
      void queryClient.resetQueries({ predicate: (query) => query.queryKey[0] !== "auth" });
    }
  }, [scopeKey, queryClient]);
  const loginMut = useLoginMutation();
  const logoutMut = useLogoutMutation();

  // While signed in, rotate the access token ahead of its expiry so an open
  // tab stays authenticated without hitting a 401. (A 401 on any request is
  // still recovered transparently by the fetch layer's refresh-on-401.)
  const isSignedIn = Boolean(meQuery.data);
  useEffect(() => {
    if (!isSignedIn) return;
    const id = setInterval(() => {
      authApi
        .refresh()
        .then((r) => setCsrfToken(r.csrfToken))
        .catch(() => {
          /* the next request's refresh-on-401 will handle a genuine logout */
        });
    }, PROACTIVE_REFRESH_MS);
    return () => clearInterval(id);
  }, [isSignedIn]);

  const login = useCallback(
    async (email: string, password: string) => {
      await loginMut.mutateAsync({ email, password });
    },
    [loginMut],
  );

  const logout = useCallback(async () => {
    await logoutMut.mutateAsync();
  }, [logoutMut]);

  const value = useMemo<AuthState>(
    () => ({
      user: meQuery.data ?? null,
      loading: meQuery.isLoading,
      login,
      logout,
    }),
    [meQuery.data, meQuery.isLoading, login, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside <AuthProvider>");
  return ctx;
}
